/**
 * Gestionnaire d'upload de fichiers pour les formulaires ACF front-end
 * Upload immédiat à la sélection, IDs stockés dans un champ caché
 */
export default class FileUploader {

    /** @param {HTMLElement} form */
    constructor(form) {
        this.form    = form;
        this.files   = [];
        this.ajaxUrl = window.modData?.ajaxUrl ?? '';
        this.nonce   = window.modData?.uploadNonce ?? '';
        this.i18n    = window.modData?.i18n ?? {};

        // Récupérer le post_id et les fichiers existants depuis le wrapper
        const wrapper  = form.closest('[data-post-id]');
        this.postId    = wrapper?.dataset.postId ?? 0;
        const existing = wrapper?.dataset.existingFiles;
        this.existingFiles = existing ? JSON.parse(existing) : [];

        this._render();
        this._bindEvents();
        this._loadExistingFiles();
    }

    _render() {
        const submitBtn = this.form.querySelector('.acf-form-submit');

        const wrapper = document.createElement('div');
        wrapper.className = 'mod-file-uploader my-4';
        wrapper.innerHTML = `
            <p class="text-sm font-semibold mb-2">${this.i18n.filesLabel ?? 'Fichiers joints'}</p>
            <div class="mod-file-uploader__list flex flex-col gap-2 mb-3"></div>
            <label class="btn btn-outline btn-sm cursor-pointer gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                ${this.i18n.addFile ?? 'Ajouter un fichier'}
                <input type="file" class="hidden" accept="*/*">
            </label>
            <input type="hidden" name="mod_attachment_ids">
        `;

        submitBtn
            ? this.form.insertBefore(wrapper, submitBtn)
            : this.form.appendChild(wrapper);

        this._listEl    = wrapper.querySelector('.mod-file-uploader__list');
        this._fileInput = wrapper.querySelector('input[type="file"]');
        this._idsInput  = wrapper.querySelector('input[type="hidden"]');
    }

    _bindEvents() {
        this._fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (!file) return;
            this._upload(file);
            e.target.value = '';
        });
    }

    /** Affiche les fichiers déjà attachés à la prestation */
    _loadExistingFiles() {
        this.existingFiles.forEach(fileData => {
            const itemEl = this._createItem(fileData.filename, 'existing');
            this._listEl.appendChild(itemEl);
            this._bindDeleteBtn(itemEl, fileData, 'existing');
        });
    }

    async _upload(file) {
        const itemEl = this._createItem(file.name, 'loading');
        this._listEl.appendChild(itemEl);

        const body = new FormData();
        body.append('action', 'mod_upload_file');
        body.append('nonce', this.nonce);
        body.append('file', file);

        try {
            const res  = await fetch(this.ajaxUrl, { method: 'POST', body });
            const data = await res.json();

            if (data.success) {
                this.files.push(data.data);
                this._syncIds();
                this._updateItem(itemEl, data.data, 'success');
                this._bindDeleteBtn(itemEl, data.data, 'new');
            } else {
                this._updateItem(itemEl, { filename: file.name }, 'error', data.data?.message);
            }
        } catch (err) {
            this._updateItem(itemEl, { filename: file.name }, 'error', err.message);
        }
    }

    /**
     * Supprime un fichier existant via AJAX (appel immédiat)
     * ou retire un nouveau fichier de la liste en attente
     */
    _bindDeleteBtn(itemEl, fileData, type) {
        const btn = document.createElement('button');
        btn.type      = 'button';
        btn.className = 'btn btn-ghost btn-xs text-error shrink-0';
        btn.setAttribute('aria-label', this.i18n.remove ?? 'Supprimer');
        btn.textContent = '✕';

        btn.addEventListener('click', async () => {
            if (type === 'existing') {
                // Suppression immédiate côté serveur
                btn.disabled = true;
                const ok = await this._deleteFromServer(fileData.attachment_id);
                if (!ok) {
                    btn.disabled = false;
                    return;
                }
            } else {
                // Nouveau fichier : retirer de la liste en attente
                this.files = this.files.filter(f => f.attachment_id !== fileData.attachment_id);
                this._syncIds();
            }
            itemEl.remove();
        });

        itemEl.appendChild(btn);
    }

    async _deleteFromServer(attachmentId) {
        const body = new FormData();
        body.append('action', 'mod_delete_file');
        body.append('nonce', this.nonce);
        body.append('attachment_id', attachmentId);
        body.append('post_id', this.postId);

        try {
            const res  = await fetch(this.ajaxUrl, { method: 'POST', body });
            const data = await res.json();
            return data.success;
        } catch {
            return false;
        }
    }

    _createItem(filename, status) {
        const el = document.createElement('div');
        el.className = 'flex items-center gap-2 p-2 rounded bg-base-200 text-sm';
        el.innerHTML = `
            <span class="shrink-0">${this._icon(status)}</span>
            <span class="flex-1 truncate">${this._escape(filename)}</span>
            <span class="text-xs text-base-content/50">
                ${status === 'loading' ? (this.i18n.uploading ?? 'Envoi…') : ''}
            </span>
        `;
        return el;
    }

    _updateItem(el, fileData, status, errorMsg = '') {
        el.querySelector('span:first-child').innerHTML = this._icon(status);
        const statusEl = el.querySelector('span:nth-child(3)');

        if (status === 'success') {
            statusEl.innerHTML = '';
        } else if (status === 'error') {
            el.classList.add('text-error');
            statusEl.textContent = errorMsg || 'Erreur';
        }
    }

    _syncIds() {
        this._idsInput.value = this.files.map(f => f.attachment_id).join(',');
    }

    _icon(status) {
        const icons = {
            loading:  `<span class="loading loading-spinner loading-xs"></span>`,
            success:  `<svg xmlns="http://www.w3.org/2000/svg" class="size-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`,
            existing: `<svg xmlns="http://www.w3.org/2000/svg" class="size-4 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>`,
            error:    `<svg xmlns="http://www.w3.org/2000/svg" class="size-4 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>`,
        };
        return icons[status] ?? '';
    }

    _escape(str) {
        return str.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
    }
}